import { ReactNode } from "react";
import { Indicator } from "@/components/ui/indicator";
import { InfoModalTrigger } from "@/components/ui/modal";
import { VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG } from "@/features/reports/modals/video-metric-definitions-modal";
import { cn } from "@/utils/classes";
import { asColor } from "@/utils/format";

/* Prop Types */
interface VideoMetricRowProps {
  label: string;
  score: number | null | undefined;
  nullLabel?: string;
  infoModalContent?: ReactNode;
  labelClassName?: string;
}

/**
 * VideoMetricRow component
 *
 * Renders a single video sub-metric (Hook / Visual ease / Hold) as a label on the left and a
 * coloured dot + percentage score on the right. When score isn't a number yet (not yet computed
 * for this scene — the API may send this as null or simply omit the field), shows a neutral dot
 * and nullLabel instead — "Later" by default. Pass infoModalContent to show an info icon that
 * opens a modal explaining the metric. Pass labelClassName to override the muted label colour.
 */
export const VideoMetricRow = ({ label, score, nullLabel = "Later", infoModalContent, labelClassName }: VideoMetricRowProps) => {
  const hasScore = typeof score === "number";

  /* Render */
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1">
        <span className={cn("text-sm text-base-content/70", labelClassName)}>{label}</span>
        {infoModalContent && <InfoModalTrigger modalContent={infoModalContent} iconClassName="size-4" config={VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG} />}
      </div>
      <div className="flex items-center gap-3">
        <Indicator color={hasScore ? asColor(score) : "#9ca3af"} size="small" />
        <span className="text-sm font-semibold whitespace-nowrap">{hasScore ? `${Math.round(score)}%` : nullLabel}</span>
      </div>
    </div>
  );
};
